import { useFormikContext } from 'formik';
import React from 'react';

import { useOnSettingsRevert } from '~settings-coordinator/SettingsCoordinatorHook';
import { CompositeSessionInfoSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { Messages } from '~src/codegen/Localize';
import { openReactConfirmationDialog } from '~src/commonInfra/dialogs/ReactConfirmationDialog';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { Tableau } from '~src/commonInfra/utils/TableauLog';
import { IPackage } from '~src/packages/models/IPackage';
import { FindPackagesByAllowedOriginService } from '~src/packages/serverApi/requests/FindPackagesByAllowedOrigin';
import { ListPackagesService } from '~src/packages/serverApi/requests/ListPackages';
import { SettingsSection } from '~src/settings/components/common/SettingsSection';

import { componentRegistryEntry } from '../../general-settings/components/SettingsComponentRegistryFunctions';
import { SiteSettingsForSiteAdminProps } from '../../general-settings/components/site-settings/ReactSiteSettingsForSiteAdmin';
import { useSelector } from '../../general-settings/utils';
import { AllowedOriginsListView } from './components/AllowedOriginsListView';
import { PackageImpactWarning } from './components/PackageImpactWarning';
import { OriginCanonicalizer } from './utils/OriginCanonicalizer';

export const ALLOWED_ORIGINS_FIELD = 'settings.siteSettingsV2.extensionPackageAllowedOrigins';
export const ALLOWED_ORIGINS_SECTION_TEST_ID = 'allowed-origins-section';

// Registration key for useOnSettingsRevert -- distinct from the page form's own name.
const ALLOWED_ORIGINS_REVERT_ID = 'AllowedExtensionOriginsSection';

export type AllowedExtensionOriginsSectionProps = SiteSettingsForSiteAdminProps<'siteSettingsV2'>;

/**
 * Client-side editor for the `extensionPackageAllowedOrigins` site setting: an add/remove
 * list of origins plus a live compliance-impact preview, integrated into the shared
 * siteSettingsV2 Formik form (see DefaultSiteLanguageSection for the same pattern) so the
 * page-level Save button persists it -- there is no section-local save.
 *
 * TODO(W-23836064 phase 5, see analysis/w-23836064-phase5-persisted-compliance-status.md):
 * writes go through the generic by-name siteSettingsV2 path with no dedicated backend
 * validation-on-write endpoint beyond the codec/validator's own strict rejection, and
 * PackageImpactWarning's compliance preview is entirely client-computed with no persisted
 * server-side compliance status to reconcile against. Revisit both once phase 5 ships.
 */
export function AllowedExtensionOriginsSection(): React.ReactElement | null {
  const formik = useFormikContext<AllowedExtensionOriginsSectionProps>();
  const showSection = useShowSection();
  const packages = usePackages(showSection);

  const stored: string = formik.values.settings?.siteSettingsV2?.extensionPackageAllowedOrigins ?? '';
  const initial: string = formik.initialValues.settings?.siteSettingsV2?.extensionPackageAllowedOrigins ?? '';

  // pendingTokens is the source of truth for rendering (it can hold blank/invalid
  // in-progress rows that the canonical `stored` string can't represent). It's pushed
  // one-way into formik on every edit; lastPushedRef lets the effect below tell "formik
  // changed because we just wrote it" apart from "formik changed for some other reason"
  // (initial load, or a page-level Cancel/reset) and only resync from formik in the latter case.
  const lastPushedRef = React.useRef<string>(stored);
  const [pendingTokens, setPendingTokens] = React.useState<string[]>(() => OriginCanonicalizer.splitOrigins(stored));

  React.useEffect(() => {
    if (stored !== lastPushedRef.current) {
      lastPushedRef.current = stored;
      setPendingTokens(OriginCanonicalizer.splitOrigins(stored));
    }
  }, [stored]);

  // The [stored] effect above misses a page-level Revert when the canonical value doesn't change
  // (an invalid/blank in-progress token is dropped from what's committed, so `stored` never
  // diverged). Re-derive pendingTokens from initial on revert so a stale row can't linger.
  useOnSettingsRevert(ALLOWED_ORIGINS_REVERT_ID, () => {
    lastPushedRef.current = initial;
    setPendingTokens(OriginCanonicalizer.splitOrigins(initial));
  });

  if (!showSection) {
    return null;
  }

  const commitTokens = (tokens: string[]) => {
    setPendingTokens(tokens);
    const canonical = OriginCanonicalizer.serialize(canonicalizeAll(tokens));
    lastPushedRef.current = canonical;
    formik.setFieldValue(ALLOWED_ORIGINS_FIELD, canonical);
  };

  // Reverse-lookup-before-removal: PackageImpactWarning already gives an ambient preview
  // from the full package list fetched on mount, but that list can go stale (e.g. another
  // admin uploads a package in another tab). Get a fresh, single-origin-scoped answer at
  // the moment of removal and let the admin confirm if it would affect anything.
  const handleBeforeDelete = (origin: string): Promise<boolean> => {
    const canonical = OriginCanonicalizer.tryCanonicalizeOrigin(origin);
    if (canonical === undefined) {
      return Promise.resolve(true);
    }
    return FindPackagesByAllowedOriginService.findPackagesByAllowedOrigin(canonical)
      .then(response => {
        const affected = response.packages?.package ?? [];
        if (affected.length === 0) {
          return true;
        }
        return openReactConfirmationDialog<void, void>({
          title: Messages.allowedOriginsRemoveConfirmTitle(),
          message: Messages.allowedOriginsRemoveConfirmMessage({
            ORIGIN: canonical,
            COUNT: affected.length.toString(),
            PACKAGE_NAMES: affected.map(pkg => pkg.name ?? pkg.manifestId ?? pkg.luid ?? '').join(', '),
          }),
          actionName: Messages.allowedOriginsRemoveConfirmAction(),
          isActionDelete: true,
          paramsForAction: undefined,
          action: () => Promise.resolve(),
          dialogWidth: TabDialogWidth.width500px,
        }).result.then(() => true, () => false);
      })
      .catch(error => {
        Tableau.Logger().error(error);
        // The reverse-lookup is a confirmation aid, not a gate -- don't block removal on it.
        return true;
      });
  };

  const currentOrigins = new Set(canonicalizeAll(OriginCanonicalizer.splitOrigins(initial)));
  const pendingOrigins = new Set(canonicalizeAll(pendingTokens));

  return (
    <SettingsSection
      title={Messages.allowedOriginsSectionTitle()}
      description={Messages.allowedOriginsSectionDescription()}
      testId={ALLOWED_ORIGINS_SECTION_TEST_ID}>
      <AllowedOriginsListView
        origins={pendingTokens}
        onChange={commitTokens}
        onBeforeDelete={handleBeforeDelete}
      />
      {packages && (
        <PackageImpactWarning
          packages={packages}
          currentOrigins={currentOrigins}
          pendingOrigins={pendingOrigins}
        />
      )}
    </SettingsSection>
  );
}

function canonicalizeAll(tokens: readonly string[]): string[] {
  return tokens
    .map(token => OriginCanonicalizer.tryCanonicalizeOrigin(token))
    .filter((canonical): canonical is string => canonical !== undefined);
}

function useShowSection(): boolean {
  const isSiteOrServerAdmin = !!useSelector(CompositeSessionInfoSelectors.currentUserIsSiteOrServerAdminSelector);
  return FeatureFlags.extensionPackageAllowedOriginsSiteSettingEnabled && isSiteOrServerAdmin;
}

function usePackages(enabled: boolean): IPackage[] | null {
  const [packages, setPackages] = React.useState<IPackage[] | null>(null);

  React.useEffect(() => {
    if (!enabled) {
      return;
    }
    let isMounted = true;
    ListPackagesService.listPackages()
      .then(response => {
        if (isMounted) {
          setPackages(response.packages?.package ?? []);
        }
      })
      .catch((error: Error) => {
        Tableau.Logger().error(error);
      });
    return () => { isMounted = false; };
  }, [enabled]);

  return packages;
}

export const AllowedExtensionOriginsSectionSpecs = componentRegistryEntry({
  name: 'AllowedExtensionOriginsSection',
  component: AllowedExtensionOriginsSection
});
